import { Flex } from "@chakra-ui/react";
import apiV2 from "api-v2";
import { TextArea } from "components/FormInputs/TextArea";
import { ModalNew } from "components/Modal/ModalNew";
import StatusPill from "components/StatusPill";
import RfxPreApprovalTour, {
	useRfxPreApprovalTour,
} from "features/tours/rfx-pre-approval-tour/RfxPreApprovalTour";
import { Form, Formik } from "formik";
import { doesRFxHaveApprovers, isRfxApproved } from "helpers/rfxHelper";
import { useCurrentUserId } from "hooks/auth";
import { useEffect, useState } from "react";
import { RFX_STATUS } from "utils/constants";
import { showToast } from "utils/helpers";
import * as Yup from "yup";

import { Button } from "newui/components/Button";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import { isRfxStatusMatch } from "newui/features/Rfx/utils";

type ApprovalActionButtonProps = {
	isLoading: boolean;
	rfx: Rfx | null;
	onClose?: () => void;
};

export const ApprovalActionButton = ({
	onClose,
	rfx,
	isLoading,
}: ApprovalActionButtonProps) => {
	const userId = useCurrentUserId();
	const approver = rfx?.rfxApprovers?.find(
		(approver) => approver.approverUserId === userId
	);
	const [additionalMessage, setAdditionalMessageChanged] = useState("");
	const [isSubmitting, setSubmitting] = useState(false);
	const [isApproved, setIsApproved] = useState(false);
	const [hasApprovers, setHasApprovers] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const { isTourVisible, setIsTourVisible, shouldShowTour } =
		useRfxPreApprovalTour();

	const handleAdditionalMessageChange = (e) => {
		setAdditionalMessageChanged(e.target.value);
	};

	useEffect(() => {
		setIsApproved(isRfxApproved(rfx));
		setHasApprovers(doesRFxHaveApprovers(rfx));
	}, [rfx]);

	useEffect(() => {
		if (!isLoading && rfx && shouldShowTour()) {
			setIsTourVisible(true);
		}
	}, [isLoading, rfx, shouldShowTour, setIsTourVisible]);

	const handleOnClose = () => {
		setIsOpen(false);
		if (onClose) {
			onClose();
		}
	};

	const handleOpenModalClick = () => {
		setIsOpen(true);
	};

	const handleSubmit = async () => {
		setSubmitting(true);

		try {
			if (!approver) {
				throw new Error(
					"Something went wrong with your approval ID, please refresh and try again."
				);
			}

			// Make the API call
			const response = await apiV2.post(`rfq/${approver.id}/approval`, {
				has_approved: true,
				additional_message: additionalMessage,
			});

			if (response?.data?.success) {
				showToast("Successfully approved Submission", "Success", true);
				approver.hasApproved = true;
				setIsApproved(true);
			}
		} catch (error) {
			console.error("Error approving RFx", error);
			showToast(error.message, "error");
			// setIsInvitedSuccess(false);
		} finally {
			setSubmitting(false);
		}
	};

	if (isLoading || !rfx) {
		return null;
	}

	// no approvers so do nothing
	if (!hasApprovers) {
		return null;
	}

	// RFx was approved successfully
	if (isApproved && hasApprovers) {
		return (
			<StatusPill
				backgroundColor="bg-blue-100"
				textColor="text-blue-900"
				borderColor="border-blue-900"
			>
				Approved for Submission
			</StatusPill>
		);
	}

	// RFx was overridden
	if (
		hasApprovers &&
		!isApproved &&
		!isRfxStatusMatch(rfx?.status, RFX_STATUS.DRAFT)
	) {
		return (
			<StatusPill
				backgroundColor="bg-red-100"
				textColor="text-red-900"
				borderColor="border-red-900"
			>
				Submission Approval was Overridden
			</StatusPill>
		);
	}

	const initialValues = { additional_message: "" };

	const typeSafeTextArea = (
		//@ts-ignore
		<TextArea
			value={additionalMessage}
			label="The RFx Lead/Author will be notified of your approval along with any comments you provide below."
			name="additional_message"
			placeholder="Additional Message Text..."
			className="flex flex-col"
			errorClassName="!mt-1"
			onChange={handleAdditionalMessageChange}
		/>
	);

	// approver has been passed (is approver user), approvers have been requested and not currently approved
	const showApprovalButton = approver && hasApprovers && !isApproved;

	return (
		<>
			<Flex
				gap={4}
				align={"center"}
			>
				{showApprovalButton && (
					<>
						{
							// @ts-ignore
							<Button
								btntype="primary"
								onClick={handleOpenModalClick}
								disabled={isSubmitting}
								data-testid="approval-action-button"
							>
								Approve RFx for Submission
							</Button>
						}
						<RfxPreApprovalTour
							isVisible={isTourVisible}
							onTourComplete={() => console.log("Tour completed")}
						/>
					</>
				)}
			</Flex>
			<Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={Yup.object({
					additional_message: Yup.string(),
				})}
				onSubmit={handleSubmit}
			>
				<ModalNew
					isOpen={isOpen}
					onClose={handleOnClose}
					headerContent="Approve Draft RFx"
					maxWidth="600px"
					useDefaultFooter
					submitText="Approve"
				>
					<Form>{typeSafeTextArea}</Form>
				</ModalNew>
			</Formik>
		</>
	);
};
